/**
 * vue-menu-aim is a Vue Directive heavily inspired by react-menu-aim and jQuery-menu-aim. All rights
 * reserved by the original authors.
 *
 * https://github.com/jasonslyvia/react-menu-aim
 * https://github.com/kamens/jQuery-menu-aim
 */
import { add, remove } from 'bianco.events';

const TIMER_NAME = '__vueMenuAimTimer';
const CONFIG_NAME = '__vueMenuAimConfig';
const MOUSE_LOCS_TRACKED = 3; // Number of past mouse locations to track
const DELAY = 300; // Delay in milliseconds when user appears to be entering submenu
const TOLERANCE = 75; // Tolerance in milliseconds - Bigger = more forgivey when entering submenu

// Consider multiple instance using ReactMenuAim, we just listen mousemove once
let mouseLocs = [];
let mousemoveListener = 0;

/**
 * Calculate the offset of a DOM element
 * @param {HTMLElement} el - The target element
 * @return {Object} An object containing the left and top offset of an element
 */
function offset(el) {
    const result = { left: 0, top: 0 };

    if (el) {
        const { left, top } = el.getBoundingClientRect();

        result.left = left + document.body.scrollLeft;
        result.top = top + document.body.scrollTop;
    }

    return result;
}

/**
 * Return offsetWidth and offsetHeight for a given element
 * @param {HTMLElement} el - The target element
 * @return {Object} An object containing the offsetWidth and offsetHeight including margin of an element
 */
function offsetDimensions(el) {
    const { marginLeft, marginTop } = el.currentStyle || getComputedStyle(el);

    return {
        width: el.offsetWidth + (parseInt(marginLeft, 10) || 0),
        height: el.offsetHeight + (parseInt(marginTop, 10) || 0),
    };
}

/**
 * Listener for mousemove events
 * @param {MouseEvent} e - The MouseEvent to be handled
 */
function handleMouseMoveDocument({ pageX, pageY }) {
    mouseLocs.push({ x: pageX, y: pageY });

    if (mouseLocs.length > MOUSE_LOCS_TRACKED) {
        mouseLocs.shift();
    }
}

/**
 * Detect if the user is moving towards the currently activated
 * submenu.
 * If the mouse is heading relatively clearly towards
 * the submenu's content, we should wait and give the user more
 * time before activating a new row. If the mouse is heading
 * elsewhere, we can immediately activate a new row.
 * We detect this by calculating the slope formed between the
 * current mouse location and the upper/lower right points of
 * the menu. We do the same for the previous mouse location.
 * If the current mouse location's slopes are
 * increasing/decreasing appropriately compared to the
 * previous's, we know the user is moving toward the submenu.
 * Note that since the y-axis increases as the cursor moves
 * down the screen, we are looking for the slope between the
 * cursor and the upper right corner to decrease over time, not
 * increase (somewhat counterintuitively).
 *
 * @param {Object} a - A coordinate pair containing x and y params
 * @param {Object} b - A coordinate pair containing x and y params
 * @return {number} The calculated slope number
 */
function slope(a, b) {
    return (b.y - a.y) / (b.x - a.x);
}

/**
 * Calculate the activation delay
 * @param {HTMLElement} menu - The root element
 * @param {Object} config - The plugin configuration
 */
function getActivateDelay(menu, config = {}) {
    const { left, top } = offset(menu);
    const { width, height } = offsetDimensions(menu);
    const tolerance = config.tolerance || TOLERANCE;

    const loc = Object.assign({}, mouseLocs[mouseLocs.length - 1]);
    let prevLoc = Object.assign({}, mouseLocs[0]);

    const upperLeft = {
        x: left,
        y: top - tolerance,
    };
    const upperRight = {
        x: left + width,
        y: upperLeft.y,
    };
    const lowerLeft = {
        x: left,
        y: top + height + tolerance,
    };
    const lowerRight = {
        x: left + width,
        y: lowerLeft.y,
    };

    if (!loc) { return 0; }
    if (!prevLoc) { prevLoc = loc; }

    if (config.isFixed) {
        loc.y = loc.y - window.pageYOffset;
        prevLoc.y = prevLoc.y - window.pageYOffset;
    }

    // If the previous mouse location was outside of the entire
    // menu's bounds, immediately activate.
    if (prevLoc.x < left || prevLoc.x > lowerRight.x || prevLoc.y < top || prevLoc.y > lowerRight.y) {
        return 0;
    }

    // If the mouse hasn't moved since the last time we checked
    // for activation status, immediately activate.
    if (menu._lastDelayDoc && loc.x === menu._lastDelayDoc.x && loc.y === menu._lastDelayDoc.y) {
        return 0;
    }

    var decreasingCorner = upperRight;
    var increasingCorner = lowerRight;

    // Our expectations for decreasing or increasing slope values
    // depends on which direction the submenu opens relative to the
    // main menu. By default, if the menu opens on the right, we
    // expect the slope between the cursor and the upper right
    // corner to decrease over time, as explained above. If the
    // submenu opens in a different direction, we change our slope
    // expectations.
    switch (config.submenuDirection) {
        case 'left':
            decreasingCorner = lowerLeft;
            increasingCorner = upperLeft;
            break;
        case 'below':
            decreasingCorner = lowerRight;
            increasingCorner = lowerLeft;
            break;
        case 'above':
            decreasingCorner = upperLeft;
            break;
        default:
            break;
    }

    const decreasingSlope = slope(loc, decreasingCorner);
    const increasingSlope = slope(loc, increasingCorner);
    const prevDecreasingSlope = slope(prevLoc, decreasingCorner);
    const prevIncreasingSlope = slope(prevLoc, increasingCorner);

    // Mouse is moving from previous location towards the
    // currently activated submenu. Delay before activating a
    // new menu row, because user may be moving into submenu.
    if (decreasingSlope < prevDecreasingSlope && increasingSlope > prevIncreasingSlope) {
        menu._lastDelayLoc = loc;
        return config.delay || DELAY;
    }

    menu._lastDelayLoc = null;

    return 0;
}

/**
 * For the concern of further changes might happen when activeRowIndex changes,
 * this mixin doesn't change state directly, instead it calls the callback provided
 * by the user.
 *
 * @param {number} rowIdentifier - A row id
 * @param {Function} handler - The callback to trigger
 */
function activate(rowIdentifier, handler) {
    handler(rowIdentifier);
}

/**
 * Check if a row is eligible to be activated
 * @param {HTMLElement} el - The root element
 * @param {number} rowIdentifier - A row id
 * @param {Object} config - The plugin configuration
 */
function possiblyActivate(el, rowIdentifier, config) {
    const delay = getActivateDelay(el, config);

    if (delay) {
        el[TIMER_NAME] = setTimeout(() => {
            possiblyActivate(el, rowIdentifier, config);
        }, delay);
    } else {
        activate(rowIdentifier, config.mouseEnter);
    }
}

/**
 * Handle a mouseleave on the menu
 * @param {MouseEvent} e - A mouseleave event
 */
function handleMouseLeaveMenu(e) {
    if (this[TIMER_NAME]) {
        clearTimeout(this[TIMER_NAME]);
    }

    if (typeof this[CONFIG_NAME].mouseLeave === 'function') {
        this[CONFIG_NAME].mouseLeave(e);
    }
}

/**
 * Handle a mouseenter on a row item
 * @param {MouseEvent} e - A mouseenter event
 */
function handleMouseEnterRow(e) {
    const index = this[CONFIG_NAME].row.map(vm => vm.$el || vm).indexOf(e.target);

    if (this[TIMER_NAME]) {
        clearTimeout(this[TIMER_NAME]);
    }

    possiblyActivate(this, index, this[CONFIG_NAME]);
}

/**
 * @export
 */
export default {
    inserted(el, { value }, vnode) {
        el[CONFIG_NAME] = value;

        if (mousemoveListener === 0) {
            add(document, 'mousemove', handleMouseMoveDocument);
        }

        add(el, 'mouseleave', handleMouseLeaveMenu.bind(el));

        mousemoveListener += 1;

        vnode.context.$nextTick(() => {
            const elements = el[CONFIG_NAME].row.map(row => row.$el || row);
            add(elements, 'mouseenter', handleMouseEnterRow.bind(el));
        });
    },

    unbind(el) {
        mousemoveListener -= 1;

        if (mousemoveListener === 0) {
            remove(document, 'mousemove', handleMouseMoveDocument);
            mouseLocs = [];
        }

        clearTimeout(el[TIMER_NAME]);
        el[TIMER_NAME] = null;
    },

    update(el, { value }) {
        el[CONFIG_NAME] = value;
    },
};



// WEBPACK FOOTER //
// ./sib-website/assets/src/js/app/vue/directives/menu-aim/index.js